import { Box, Button, Typography } from "@mui/material";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Layout from "@/components/layout";
import { ArtistNav } from "@/components/templates/ArtistTemplate/ArtistNav";
import { forceRefresh } from "@/lib/versionManager";

/**
 * Shown when a route threw on a build that is already current, so a reload
 * fetches the same code and the 404 page would be a lie.
 */
export function RouteErrorScreen() {
  const [isReloading, setIsReloading] = useState(false);

  const handleReload = () => {
    if (isReloading) return;
    setIsReloading(true);
    forceRefresh();
  };

  return (
    <Layout>
      <ArtistNav artist="Truevined" />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "var(--backgroundColor)",
          pt: { xs: "12rem", md: "10rem" },
          pb: { xs: "4rem", md: "6rem" },
          px: { xs: "2.5rem", md: "5rem" },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          component="section"
          role="alert"
          sx={{
            width: "100%",
            maxWidth: "34rem",
            borderRadius: "16px",
            border: "1px solid color-mix(in srgb, var(--logoColor) 26%, transparent)",
            backgroundColor: "color-mix(in srgb, var(--logoColor) 8%, transparent)",
            padding: { xs: "24px 20px", md: "32px" },
            alignSelf: "flex-start",
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              color: "var(--logoColor2)",
              backgroundColor: "color-mix(in srgb, var(--logoColor) 16%, transparent)",
              "& svg": { width: "20px", height: "20px" },
            }}
          >
            <AlertTriangle />
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: "Hanson, sans-serif",
              fontWeight: 700,
              fontSize: { xs: "28px", md: "clamp(28px, 2.25rem, 40px)" },
              lineHeight: 1.1,
              color: "var(--logoColor2)",
              mt: "1.25rem",
            }}
          >
            Something went wrong
          </Typography>

          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "17px", md: "clamp(16px, 1.1rem, 19px)" },
              lineHeight: 1.7,
              color: "var(--logoColor)",
              mt: "0.75rem",
            }}
          >
            This page could not finish loading. A reload usually clears it. If it keeps happening, head back to the music and
            try this page again later.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", mt: "1.75rem" }}>
            <Button
              type="button"
              onClick={handleReload}
              disabled={isReloading}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "15px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderRadius: "999px",
                px: "26px",
                minHeight: "48px",
                color: "var(--color1)",
                backgroundColor: "var(--logoColor2)",
                "&:hover": { backgroundColor: "var(--logoColor)" },
                "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
                "&.Mui-disabled": { opacity: 0.7, color: "var(--color1)" },
              }}
            >
              {isReloading ? "Reloading" : "Reload"}
            </Button>
            <Button
              component={RouterLink}
              to="/"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "15px",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                borderRadius: "999px",
                px: "24px",
                minHeight: "48px",
                color: "var(--logoColor2)",
                border: "1px solid color-mix(in srgb, var(--logoColor) 34%, transparent)",
                "&:hover": { backgroundColor: "color-mix(in srgb, var(--logoColor) 12%, transparent)" },
                "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
              }}
            >
              Back to the music
            </Button>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
}

export default RouteErrorScreen;
